import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { rectSwappingStrategy, SortableContext } from '@dnd-kit/sortable';
import { AdImagesItem } from './AdImagesItem';
import * as S from './AdImages.styles';
import { HelperText } from '@/components/UI/HelperText/HelperText';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { GalleryDrawer } from '@/components/GalleryDrawer/GalleryDrawer';
import { uuidShort } from '@/utils/uuid-generators';
import {
  AdImagesProps,
  AdImagesRef,
} from '@/components/AdImages/AdImages.types';
import { useImagesList } from '@/hooks/useImagesList';

export const AdImages = React.forwardRef<AdImagesRef, AdImagesProps>(
  ({ hasPresetImages, helperText, hasError, value, onChange }, adImagesRef) => {
    const { t } = useTranslation();
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);

    const {
      images,
      setImages,
      sensors,
      onDragEnd,
      onRemove,
      onFileInputChange,
      fileInputRef,
      triggerAdd: triggerFileInput,
    } = useImagesList(value);

    function toggleGallery() {
      setIsGalleryOpen(prev => !prev);
    }

    function triggerAdd() {
      if (hasPresetImages) {
        toggleGallery();
      } else {
        triggerFileInput();
      }
    }

    const AdImagesButton = ({ isFullWidth }: { isFullWidth?: boolean }) => {
      return (
        <S.AdImagesAddButton
          type="button"
          onClick={triggerAdd}
          $isFullWidth={isFullWidth}
        >
          {hasPresetImages ? (
            <IconBox $filename="gallery.svg" $width={35} $height={31} />
          ) : (
            <>
              <input
                type="file"
                ref={fileInputRef}
                onChange={onFileInputChange}
                multiple
              />
              <IconBox $filename="camera.svg" $width={37} $height={29} />
            </>
          )}

          {t(
            isFullWidth
              ? hasPresetImages
                ? 'pages.new_ad.choose_photo'
                : 'pages.new_ad.add_photo'
              : hasPresetImages
              ? 'pages.new_ad.choose'
              : 'pages.new_ad.add'
          )}
        </S.AdImagesAddButton>
      );
    };

    const onApplyGallery = useCallback((amount: number) => {
      // temporary for mocks
      setImages(
        new Array(amount)
          .fill(null)
          .map(() => ({ id: uuidShort(), src: '/mock-product.png' }))
      );
    }, []);

    useEffect(() => {
      onChange(images);
    }, [images]);

    useImperativeHandle(
      adImagesRef,
      () => ({
        reset: () => {
          setImages([]);
        },
      }),
      []
    );

    return (
      <>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <S.AdImagesGrid>
            <SortableContext items={images} strategy={rectSwappingStrategy}>
              {images.map(image => {
                return (
                  <AdImagesItem
                    key={image.id}
                    image={image}
                    onRemove={onRemove}
                    hasRotationControls={!hasPresetImages}
                  />
                );
              })}
            </SortableContext>
            {!!images.length && <AdImagesButton />}
          </S.AdImagesGrid>
        </DndContext>

        {!images.length && <AdImagesButton isFullWidth />}
        <S.AdImagesCaption $variant="1">
          {t('pages.new_ad.add_photo_text_1')}
        </S.AdImagesCaption>
        <S.AdImagesCaption $variant="1">
          {hasPresetImages
            ? t('pages.new_ad.add_photo_text_3')
            : t('pages.new_ad.add_photo_text_2')}
        </S.AdImagesCaption>

        {helperText && (
          <HelperText
            helperText={helperText}
            hasError={hasError}
            hasBottomMargin={false}
          />
        )}

        {hasPresetImages && (
          <GalleryDrawer
            isOpen={isGalleryOpen}
            close={toggleGallery}
            onApply={onApplyGallery}
          />
        )}
      </>
    );
  }
);

AdImages.displayName = 'AdImages';
